import React from 'react';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import StaticImageSlideshow from './staticimageslideshow';
import { Shuffle, Check, Palette, Share2, Zap, X } from 'lucide-react';

// Profile dialog for the Strava-webhook auto-generate feature. Bundles
// two controls behind one entry point so the profile popover only needs
// a single button:
//
//   1. On/off toggle for the webhook subscription (calls `onToggleAutoGenerate`).
//   2. Style picker — Random tile clears the pin; the slideshow tiles
//      save the underlying settings dict so the choice survives renames
//      or deletions of the source style. Style controls are hidden when
//      auto-generate is off.
const WebhookStylePicker = ({
  open,
  onOpenChange,
  autoGenerate,
  onToggleAutoGenerate,
  currentLabel,
  onSave,
  onClear,
}) => {
  const isRandom = !currentLabel;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl p-0 overflow-hidden w-[90vw] sm:w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 text-white px-6 py-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            <Zap className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-lg font-semibold">Auto-generate maps</DialogTitle>
            <p className="text-xs text-gray-300 mt-0.5">
              When a new Strava activity comes in, render a map and email it to you — for free.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-4">
          {/* On/off — same toggle that used to live as a checkbox in the
              profile popover. Always rendered so the user can disable
              the feature without leaving the dialog. */}
          <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-gray-200 p-3 hover:bg-gray-50 transition">
            <input
              type="checkbox"
              checked={!!autoGenerate}
              onChange={onToggleAutoGenerate}
              className="mt-0.5 h-4 w-4"
            />
            <span className="text-sm">
              Auto-generate from latest Strava activity
              <span className="block text-xs text-gray-500 mt-0.5">
                Toggle on to start receiving auto-rendered maps via email
                whenever a new Strava activity is uploaded.
              </span>
            </span>
          </label>

          {autoGenerate && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5 pt-1">
                <Palette className="h-3.5 w-3.5" /> Map style
              </div>

              <button
                type="button"
                onClick={() => { onClear(); }}
                className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition
                  ${isRandom ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <div className="h-10 w-10 rounded-full bg-gray-900 text-white flex items-center justify-center shrink-0">
                  <Shuffle className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">Random (any style)</div>
                  <div className="text-xs text-gray-500">
                    Each webhook render picks a fresh style from the pool.
                  </div>
                </div>
                {isRandom && <Check className="h-4 w-4 text-blue-600" />}
              </button>

              {!isRandom && (
                <div className="text-xs text-gray-600">
                  Current pick: <span className="font-medium text-gray-900">{currentLabel}</span>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 p-1">
                <StaticImageSlideshow
                  onSettingsChange={(settings, name) => {
                    onSave(settings, name || '');
                  }}
                />
              </div>

              {/* Hint: own styles only show up here once they've been shared
                  from an unlocked map (Your Maps → share icon). */}
              <div className="flex items-start gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 text-xs text-gray-600">
                <Share2 className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
                <p>
                  Want to use one of your own styles here? Open it in
                  <span className="font-medium"> Your Maps</span>, hit the share icon
                  on any unlocked map, and it'll appear in this list.
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookStylePicker;
